import React, { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import MarketSelectorHeader from "components/ui/MarketSelectorHeader";
import DataRefreshIndicator from "components/ui/DataRefreshIndicator";
import ExportControlPanel from "components/ui/ExportControlPanel";
import FilterPersistenceManager from "components/ui/FilterPersistenceManager";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

import SubjectPropertyCard from "./components/SubjectPropertyCard";
import ComparableScoreCard from "./components/ComparableScoreCard";
import ComparisonTable from "./components/ComparisonTable";
import PropertyMap from "./components/PropertyMap";
import ValuationSummaryBar from "./components/ValuationSummaryBar";
import PriceDistributionChart from "./components/PriceDistributionChart";

export default function PropertyComparisonHub() {
  const location = useLocation();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [removedCount, setRemovedCount] = useState(0);
  const [lastMarket, setLastMarket] = useState(null);

  const handleRefresh = useCallback(async () => {
    await new Promise((r) => setTimeout(r, 800));
    setRefreshKey((k) => k + 1);
  }, []);

  const handleExport = useCallback(async (option) => {
    await new Promise((r) => setTimeout(r, 1200));
    console.log("Exporting:", option?.label);
  }, []);

  const handleMarketChange = useCallback((market) => {
    setLastMarket(market);
    setRefreshKey((k) => k + 1);
  }, []);

  const handleRemoveComp = useCallback(() => {
    setRemovedCount((c) => c + 1);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <MarketSelectorHeader onMarketChange={handleMarketChange} />
      {/* Page content — offset for fixed header (60px) + tab nav (49px) on desktop */}
      <main className="pt-[60px] lg:pt-[109px] pb-20 lg:pb-8">
        {/* Global Controls Bar */}
        <div className="sticky top-[60px] lg:top-[109px] z-40 bg-card border-b border-border px-4 md:px-6 lg:px-8 py-2.5">
          <div className="max-w-screen-2xl mx-auto flex items-center justify-between gap-3 flex-wrap">
            {/* Left: Title + compliance */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="hidden sm:flex items-center gap-2">
                <Icon name="BarChart2" size={16} color="var(--color-primary)" />
                <span className="font-heading font-semibold text-sm text-foreground whitespace-nowrap">Property Comparison Hub</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-caption bg-secondary/10 text-secondary px-2 py-0.5 rounded-full border border-secondary/20 whitespace-nowrap">
                  MLS Compliant
                </span>
                {lastMarket && (
                  <span className="text-xs font-caption bg-muted text-muted-foreground px-2 py-0.5 rounded-full border border-border whitespace-nowrap hidden md:inline">
                    {lastMarket?.label}
                  </span>
                )}
              </div>
            </div>

            {/* Right: Controls */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Auto-refresh toggle */}
              <button
                onClick={() => setAutoRefresh((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-caption font-medium transition-standard ${
                  autoRefresh ? "bg-secondary/10 border-secondary/30 text-secondary" : "bg-muted border-border text-muted-foreground"
                }`}
                aria-label="Toggle auto-refresh"
              >
                <Icon name="RefreshCw" size={12} color="currentColor" />
                <span className="hidden sm:inline">Auto-refresh</span>
                <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? "bg-secondary" : "bg-muted-foreground"}`} />
              </button>

              <DataRefreshIndicator onRefresh={handleRefresh} />

              <FilterPersistenceManager currentPath={location?.pathname} />

              <ExportControlPanel currentPath={location?.pathname} onExport={handleExport} />

              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                iconSize={13}
              >
                <span className="hidden sm:inline">Add Comparable</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6">

          {/* Valuation Summary Bar */}
          <section className="mb-4 md:mb-6" aria-label="Valuation summary metrics">
            <ValuationSummaryBar key={refreshKey} />
          </section>

          {/* 3-column layout on desktop */}
          <div className="flex flex-col lg:flex-row gap-4 md:gap-6 mb-4 md:mb-6">

            {/* Left: Subject Property (6 cols) */}
            <aside className="w-full lg:w-72 xl:w-80 flex-shrink-0">
              <SubjectPropertyCard onEdit={() => setEditModalOpen(true)} />
            </aside>

            {/* Center: Comparison Table (12 cols) */}
            <div className="flex-1 min-w-0 flex flex-col gap-4 md:gap-6">
              <ComparisonTable key={refreshKey} onRemove={handleRemoveComp} />
              <PriceDistributionChart key={`chart-${refreshKey}`} />
            </div>

            {/* Right: Comparable Scoring (6 cols) */}
            <aside className="w-full lg:w-72 xl:w-80 flex-shrink-0">
              <ComparableScoreCard onViewAll={() => setViewAllOpen(true)} />
            </aside>
          </div>

          {/* Full-width Map */}
          <section aria-label="Property location map">
            <PropertyMap key={`map-${refreshKey}`} />
          </section>

          {/* Removed comps notice */}
          {removedCount > 0 && (
            <div className="mt-4 flex items-center gap-3 bg-warning/10 border border-warning/30 rounded-lg px-4 py-3">
              <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
              <p className="text-sm font-caption text-foreground">
                {removedCount} comparable{removedCount > 1 ? "s" : ""} removed from analysis.
              </p>
              <Button variant="ghost" size="sm" onClick={() => { setRemovedCount(0); setRefreshKey((k) => k + 1); }} className="ml-auto">
                Restore All
              </Button>
            </div>
          )}
        </div>
      </main>
      {/* Edit Subject Property Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground/40" onClick={() => setEditModalOpen(false)} />
          <div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg text-foreground">Edit Subject Property</h2>
              <button onClick={() => setEditModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-standard">
                <Icon name="X" size={16} color="currentColor" />
              </button>
            </div>
            <p className="text-sm font-caption text-muted-foreground mb-4">
              Update the subject property details to recalculate comparable analysis and AVM estimates.
            </p>
            <div className="space-y-3">
              {["MLS ID", "Address", "List Price", "Square Footage"]?.map((field) => (
                <div key={field}>
                  <label className="block text-xs font-caption font-medium text-muted-foreground mb-1">{field}</label>
                  <input
                    type="text"
                    placeholder={`Enter ${field?.toLowerCase()}`}
                    className="w-full h-9 px-3 text-sm font-caption bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-standard"
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setEditModalOpen(false)} fullWidth>Cancel</Button>
              <Button variant="default" onClick={() => setEditModalOpen(false)} fullWidth>Save Changes</Button>
            </div>
          </div>
        </div>
      )}
      {/* View All Comparables Modal */}
      {viewAllOpen && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground/40" onClick={() => setViewAllOpen(false)} />
          <div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg text-foreground">All Comparable Matches</h2>
              <button onClick={() => setViewAllOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-standard">
                <Icon name="X" size={16} color="currentColor" />
              </button>
            </div>
            <p className="text-sm font-caption text-muted-foreground mb-4">
              Showing top 5 algorithm-matched comparables within 1.5 mile radius. Scores calculated using location, size, condition, and feature similarity.
            </p>
            <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-lg px-4 py-3 mb-4">
              <Icon name="Info" size={16} color="var(--color-primary)" />
              <p className="text-xs font-caption text-foreground">
                Algorithm uses 12 weighted factors including proximity, price range, property type, and recent sales velocity.
              </p>
            </div>
            <Button variant="default" onClick={() => setViewAllOpen(false)} fullWidth>Close</Button>
          </div>
        </div>
      )}
    </div>
  );
}